/**
 * @hook usePermissions
 * @description 
 * Decodes the JWT from storage to determine the user's granular capabilities.
 * 
 * Standards:
 * - 7.1: Zero-Trust (Client-side checks are visual only).
 * - 18.2: Developer Experience (Simple 'can' API).
 */

import { useMemo } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { storage } from '../utils/storage';
import MoksyLogger from '../utils/logger';

const parseJwtPayload = (token) => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
      .join('')
  );
  return JSON.parse(json);
};

export const usePermissions = () => {
  const { user, activeRole, availableRoles } = useAuthContext();

  return useMemo(() => {
    // 1. Fail fast if no user context
    if (!user) return { can: () => false, scopes: [], isAdmin: false };

    // 2. Effective role: prefer activeRole (set by role-switch) over primary role
    const primaryRole = String(user?.role || 'user').trim().toLowerCase();
    const safeActiveRole = Array.isArray(availableRoles) && availableRoles.includes(activeRole)
      ? String(activeRole || '').trim().toLowerCase()
      : null;
    const effectiveRole = safeActiveRole || primaryRole;

    // 3. Prefer server-hydrated scopes from the auth context.
    // Fall back to the token only for legacy or partially hydrated sessions.
    let scopes = Array.isArray(user?.scopes) ? user.scopes : [];

    if (!scopes.length) {
      const token = storage.get(storage.KEYS.ACCESS_TOKEN);
      try {
        if (token) {
          const decoded = parseJwtPayload(token);
          scopes = Array.isArray(decoded?.scopes) ? decoded.scopes : [];
        }
      } catch (e) {
        if (import.meta.env.DEV) MoksyLogger.error('PERMISSIONS.DECODE_FAILURE', e);
      }
    }

    const normalizedScopes = Array.from(new Set(scopes.filter(Boolean)));
    const isElevatedRole = effectiveRole === 'admin' || effectiveRole === 'super_admin';

    // 4. Helper function for UI checks
    const can = (actionSlug) => {
      if (isElevatedRole) return true;
      return normalizedScopes.includes(actionSlug);
    };

    return {
      can,
      scopes: normalizedScopes,
      isAdmin: isElevatedRole,
      effectiveRole
    };
  }, [user, activeRole, availableRoles]);
};
